import React, { useRef, useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/contact`;
const initial = { name: '', email: '', phone: '', message: '', website: '' };
const inputClass = 'w-full bg-transparent border-0 border-b border-gray-300 rounded-none px-0 py-3 text-gray-900 text-lg font-light placeholder-gray-300 focus:outline-none focus:border-gray-900 transition-colors duration-300';

const Field = ({ id, label, optional, children }) => (
  <div>
    <label htmlFor={id} className="block text-gray-500 text-sm mb-1 uppercase tracking-wide">
      {label}{optional && <span className="normal-case tracking-normal text-gray-400"> (valgfrit)</span>}
    </label>
    {children}
  </div>
);

export const ContactForm = () => {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const startedAt = useRef(Date.now());

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError('');
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, elapsed_ms: Date.now() - startedAt.current }),
      });
      if (!res.ok) {
        const msg = res.status === 429 ? 'Du har sendt for mange forespørgsler. Prøv igen senere.'
          : res.status === 422 ? 'Tjek venligst, at alle felter er udfyldt korrekt.'
          : 'Beskeden kunne ikke sendes. Prøv igen, eller kontakt os på telefon eller e-mail.';
        throw new Error(msg);
      }
      setStatus('sent');
      setForm(initial);
    } catch (err) {
      setStatus('error');
      setError(err.message.startsWith('Failed') ? 'Beskeden kunne ikke sendes. Prøv igen, eller kontakt os på telefon eller e-mail.' : err.message);
    }
  };

  if (status === 'sent') {
    return (
      <div data-testid="contact-form-success" className="pt-2">
        <Check className="w-6 h-6 text-gray-900 mb-6" />
        <p className="text-2xl font-light text-gray-900 mb-3">Tak for din forespørgsel</p>
        <p className="text-gray-600 leading-relaxed">Vi har modtaget din besked og vender tilbage hurtigst muligt.</p>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} className="space-y-10" noValidate={false}>
      <Field id="cf-name" label="Navn">
        <input id="cf-name" data-testid="contact-form-name" name="name" required minLength={2} maxLength={100} value={form.name} onChange={update} autoComplete="name" className={inputClass} />
      </Field>
      <Field id="cf-email" label="E-mail">
        <input id="cf-email" data-testid="contact-form-email" type="email" name="email" required value={form.email} onChange={update} autoComplete="email" className={inputClass} />
      </Field>
      <Field id="cf-phone" label="Telefonnummer" optional>
        <input id="cf-phone" data-testid="contact-form-phone" type="tel" name="phone" maxLength={30} value={form.phone} onChange={update} autoComplete="tel" className={inputClass} />
      </Field>
      <Field id="cf-message" label="Beskrivelse af projektet">
        <textarea id="cf-message" data-testid="contact-form-message" name="message" required minLength={10} maxLength={5000} rows={5} value={form.message} onChange={update} className={`${inputClass} resize-none`} />
      </Field>
      <div aria-hidden="true" className="absolute -left-[9999px] w-px h-px overflow-hidden">
        <label htmlFor="cf-website">Lad dette felt være tomt</label>
        <input id="cf-website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={update} />
      </div>
      {status === 'error' && (
        <p data-testid="contact-form-error" role="alert" className="text-sm text-red-800">{error}</p>
      )}
      <div className="space-y-6">
        <button type="submit" data-testid="contact-form-submit" disabled={status === 'sending'} className="inline-flex items-center space-x-3 text-gray-900 text-lg font-light border-b border-gray-900 pb-2 hover:text-gray-600 hover:border-gray-600 transition-colors duration-300 group disabled:opacity-40">
          <span>{status === 'sending' ? 'Sender…' : 'Send forespørgsel'}</span>
          <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
        </button>
        <p data-testid="contact-form-privacy" className="text-xs text-gray-400 leading-relaxed max-w-md">
          Dine oplysninger bruges kun til at besvare din forespørgsel. De sendes til Fryd! via vores e-mailudbyder og gemmes ikke i en database på hjemmesiden.
          Vi opbevarer henvendelsen i vores indbakke, så længe det er nødvendigt for at behandle den. Du kan altid bede om indsigt eller sletning på jh@ffryd.dk.
        </p>
      </div>
    </form>
  );
};
